import React, { useEffect, useMemo, useState } from 'react'
import { Search, Close, Sort, KeyboardArrowDown, KeyboardArrowUp, ChevronLeft, PersonAdd, Delete } from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { Box, IconButton, TextField, Menu, MenuItem, Modal, Typography, 
    TextareaAutosize, Button, CircularProgress, Stack, Skeleton, FormControl, InputLabel, Select,
    Autocomplete
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { v4 as uuidv4 } from 'uuid'

import { PatientTreeNode, Patient } from '../../utils/types'
import { modalStyle } from '../../styles/js/common'
import PatientLogs from './PatientLogs'
import PatientTree from './PatientTree'
import useResizeObserver from 'use-resize-observer'
import { get, post, put, remove } from '../../utils/http'

interface Props {
    handleShowList: Function
    showViewer: Function
}

const initialFormState: Patient = {
    id: 0,
    patient_id: '',
    name: '',
    mrn: '',
    dob: '',
    folder: '',
    location: '',
    description: '',
    slides: [],
    status: 'treatment',
    gleason_primary: '',
    gleason_secondary: '',
    clinical_stage: '',
    created: ''
}

const PatientsList = ({ handleShowList, showViewer }: Props) => {
    const [patientTree, setPatientTreeData] = useState<PatientTreeNode>({})
    const [searchOpen, setSearchOpen] = useState<boolean>(false)
    const [newPatientModal, setNewPatientModal] = useState<boolean>(false)
    const [searchKey, setSearchKey] = useState<string>('')
    const [sortMenuEl, setSortMenuEl] = useState<HTMLElement | null>(null)
    const [nodeMenuEl, setNodeMenuEl] = useState<HTMLElement | null>(null)
    const [sortType, setSortType] = useState<string | null>(null)
    const [selectedPatient, setSelectedPatient] = useState<string | null>(null)
    const [logsOpen, setLogsOpen] = useState<boolean>(false)
    const [focusNodeId, setFocusNodeId] = useState<string>('')  // eslint-disable-line
    const [patientForm, setPatientForm] = useState<Patient>(initialFormState)
    const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
    const [delModal, setDelModal] = useState<boolean>(false)
    const [folderOptions, setFolderOptions] = useState<string[]>([])

    const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>()

    const userId: number = Number(localStorage.getItem("user_id") || "")

    const sortMenuOpen = Boolean(sortMenuEl)
    const nodeMenuOpen = Boolean(nodeMenuEl)

    const handleSearch = () => setSearchOpen(true)
    const handleSearchKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchKey(e.target.value)
    const closeSearchModal = () => {
        setSearchKey('')
        setSearchOpen(false)
    }

    const handleNewPatient = () => {
        setIsFormLoading(false)
        setPatientForm(initialFormState)
        setNewPatientModal(prevState => !prevState)
    }

    const handleDelPatient = () => setDelModal(prevState => !prevState)

    const handleSortMenu = (e: React.MouseEvent<HTMLElement>) => setSortMenuEl(e.currentTarget)
    const closeSortMenu = () => setSortMenuEl(null)
    const handleSortTree = (val: string) => setSortType(val)

    const handleNodeMenu = (e: React.MouseEvent<HTMLElement>) => setNodeMenuEl(e.currentTarget)
    const closeNodeMenu = () => setNodeMenuEl(null)

    const getPatientData = async () => {
        try {
            const res: Patient = await get(`Patients/${patientTree[selectedPatient!].id}`)
            return { ...res, dob: res.dob ? res.dob.split(' ')[0] : '' }
        } catch (err) {
            const temp = {...initialFormState}
            temp.location = 'moon'
            return temp
        }
    }

    const editHandler = async () => {
        closeNodeMenu()
        setPatientForm(prevForm => ({...prevForm, status: 'loading'}))
        setNewPatientModal(prevState => !prevState)
        const data = await getPatientData()
        setPatientForm(data)
    }

    const handleShowLogs = () => {
        closeNodeMenu()
        setLogsOpen(prevState => !prevState)
    }

    const getPatientsList = async () => {
        try {
            const res = await get('Patients/tree')
            if (!Object.keys(res).length) throw Error('Empty patient list')
            setPatientTreeData(res)
        } catch (err) {
            console.log(err)
            setPatientTreeData({'none': {id: 'none', name: '', created: ''}})
        }
    }

    useEffect(() => {
        getPatientsList()
    }, [])

    const findFolders = () => {
        try {
            const temp = []
            for (let i in patientTree) {
                if (i.endsWith('/')) temp.push(i.split('/')[0])
            }
            setFolderOptions(temp)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        findFolders()
    }, [patientTree])   // eslint-disable-line

    const sortList = () => {
        if (!sortType) return

        if (sortType.startsWith('name')) {
            setPatientTreeData(prevItems => {
                const temp = {...prevItems}
                const keys = Object.keys(temp).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                if (sortType.endsWith('desc')) keys.reverse()
                return keys.reduce((acc: any, key: string) => {
                    acc[key] = temp[key]
                    return acc
                }, {})
            })
        } else {  // Date sort
            setPatientTreeData(prevItems => {
                const temp = {...prevItems}
                return Object.entries(temp).sort(([, a], [, b]) =>
                    sortType.endsWith('asc')
                    ? new Date(a.created).getTime() - new Date(b.created).getTime()
                    : new Date(b.created).getTime() - new Date(a.created).getTime()
                ).reduce((acc: any, [key, value]) => {
                    acc[key] = value
                    return acc
                }, {})
            })
        }
    }

    useEffect(() => {
        sortList()
    }, [sortType])  // eslint-disable-line

    useEffect(() => {
        if (selectedPatient && !Number.isNaN(Number(patientTree[selectedPatient].id))) showViewer(patientTree[selectedPatient].id)
    }, [selectedPatient])   // eslint-disable-line

    const selectNode = (nodePath: string) => setSelectedPatient(nodePath)

    const MemoizedTree = useMemo(() =>
        <PatientTree
            patientDict={patientTree}
            width={width}
            height={height}
            selectNode={selectNode}
            focusNodeId={focusNodeId}
            searchTerm={searchKey}
            selectedNode={selectedPatient}
        />
    , [patientTree, width, height, focusNodeId, searchKey, selectedPatient])

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent<string>) => {
        const { name, value } = e.target
        setPatientForm(prevForm => ({...prevForm, [name]: value}))
    }

    const updatePatientTree = (data: Patient, update: boolean = false) => {
        try {
            const hasFolder = Boolean(data.folder)
            const path = hasFolder ? `${data.folder}/${data.patient_id}` : data.patient_id

            setPatientTreeData(prevDict => {
                const newDict = { ...prevDict }

                // Remove the old path if it has changed to avoid duplicates
                if (update && selectedPatient && selectedPatient !== path) delete newDict[selectedPatient]

                // Set the new/updated patient data
                newDict[path] = {
                    id: String(data.id),
                    name: data.patient_id,
                    created: data.created
                }

                // If a folder exists, ensure the folder node is also present
                if (hasFolder) {
                    const folderPath = `${data.folder}/`
                    newDict[folderPath] = newDict[folderPath] || {
                        id: uuidv4(),
                        name: data.folder,
                        created: data.created
                    }
                }

                return newDict
            })
        } catch (err) {
            console.error(err)
        }
    }

    const handleSavePatient = async () => {
        setIsFormLoading(true)
        try {
            if (patientForm.id > 0) {
                const res = await put(`Patients/${patientForm.id}`, patientForm)
                updatePatientTree(res, true)
            } else {
                const payload: any = {...patientForm}
                payload.created_by = userId
                const res = await post('Patients', payload)
                updatePatientTree(res)
            }
        } catch (err) {
            console.log(err)
        } finally {
            setIsFormLoading(false)
            handleNewPatient()
        }
    }

    const delPatient = async () => {
        setIsFormLoading(true)
        try {
            await remove(`Patients/${patientTree[selectedPatient!].id}`)
            const temp = {...patientTree}
            delete temp[selectedPatient!]
            setPatientTreeData(temp)
            setSelectedPatient(null)
        } catch (err) {
            console.log(err)
        } finally {
            setIsFormLoading(false)
            handleDelPatient()
        }
    }

    return (
        <Box className='d-flex flex-column h-100 w-100'>
            <Box className='d-flex justify-content-end p-2 gap-2'>
                <IconButton onClick={handleSearch} aria-label="search">
                    <Search />
                </IconButton>
                {searchOpen
                    ? <Box className='d-flex gap-2 align-items-center justify-content-center flex-grow-1'>
                        <TextField
                            defaultValue={searchKey}
                            onChange={handleSearchKeyChange}
                            name='name'
                            label="Name"
                            variant="outlined"
                            autoComplete="off"
                            size="small"
                            fullWidth
                        />
                        <IconButton onClick={closeSearchModal} aria-label="close search"><Close /></IconButton>
                    </Box>
                    : <>
                        <IconButton onClick={handleNewPatient} aria-label="Add">
                            <PersonAdd />
                        </IconButton>
                        <IconButton onClick={handleDelPatient} aria-label="Delete" disabled={selectedPatient ? Number.isNaN(Number(patientTree[selectedPatient]?.id)) : true}>
                            <Delete />
                        </IconButton>
                        <Box>
                            <IconButton onClick={handleSortMenu} aria-haspopup="true">
                                <Sort />
                            </IconButton>
                            <Menu anchorEl={sortMenuEl} open={sortMenuOpen} onClose={closeSortMenu}>
                                <MenuItem onClick={() => handleSortTree('name-asc')}>Name&nbsp;<KeyboardArrowDown/></MenuItem>
                                <MenuItem onClick={() => handleSortTree('name-desc')}>Name&nbsp;<KeyboardArrowUp/></MenuItem>
                                <MenuItem onClick={() => handleSortTree('date-asc')}>Date&nbsp;<KeyboardArrowDown/></MenuItem>
                                <MenuItem onClick={() => handleSortTree('date-desc')}>Date&nbsp;<KeyboardArrowUp/></MenuItem>
                            </Menu>
                        </Box>
                        <Box>
                            <IconButton onClick={handleNodeMenu} disabled={selectedPatient ? Number.isNaN(Number(patientTree[selectedPatient]?.id)) : true} aria-haspopup="true">
                                <MenuIcon />
                            </IconButton>
                            <Menu anchorEl={nodeMenuEl} open={nodeMenuOpen} onClose={closeNodeMenu}>
                                <MenuItem onClick={editHandler}>Edit</MenuItem>
                                <MenuItem onClick={handleShowLogs}>View Log</MenuItem>
                            </Menu>
                        </Box>
                    </>
                }
                <IconButton onClick={() => handleShowList()} aria-label='Minimize directory' color="primary">
                    <ChevronLeft />
                </IconButton>
            </Box>

            <Box ref={ref} className='tree-parent'>{MemoizedTree}</Box>

            <Modal open={logsOpen} onClose={handleShowLogs}>
                <Box sx={modalStyle}>
                    <PatientLogs patient={patientTree[selectedPatient!]} />
                </Box>
            </Modal>

            <Modal open={delModal} onClose={handleDelPatient}>
                <Box sx={modalStyle}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">Confirm to Delete Patient</Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>{selectedPatient}</Typography>
                    <Box className='mt-3 d-flex justify-content-center'>
                        <Button className='px-4' variant="contained" onClick={delPatient} disabled={isFormLoading} color="error">
                            {isFormLoading ? <CircularProgress size={24.5} /> : 'Confirm'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={newPatientModal}
                onClose={handleNewPatient}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    {patientForm.location==='moon'
                        ? <Typography id="modal-modal-title" variant="h6" component="h2">
                            Patient not Found
                        </Typography>
                        : patientForm.status==='loading'
                            ? <Stack>
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </Stack>
                            : <>
                                <Typography id="modal-modal-title" variant="h6" component="h2">
                                    {patientForm.id > 0 ? 'Edit Patient' : 'Add Patient'}
                                </Typography>
                                <Box className='mt-3 d-flex gap-3 flex-wrap justify-content-between align-items-between'>
                                    <Box className='d-flex flex-column gap-3 flex-grow-1'>
                                        <TextField
                                            label="Id"
                                            name='patient_id'
                                            variant="outlined"
                                            value={patientForm.patient_id}
                                            onChange={handleFormChange}
                                            sx={{width: '100%'}}
                                            required
                                        />
                                        <Autocomplete
                                            freeSolo
                                            autoSelect
                                            options={folderOptions}
                                            value={patientForm.folder}
                                            onChange={(e, val) => {
                                                handleFormChange({ target: { name: "folder", value: val || '' } } as any)
                                            }}
                                            renderInput={(params) => (<TextField {...params} label="Select or Create New Folder" />)}
                                        />
                                        <TextField
                                            label="Name"
                                            name='name'
                                            variant="outlined"
                                            value={patientForm.name || ''}
                                            onChange={handleFormChange}
                                            sx={{width: '100%'}}
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                name='status'
                                                label="Status"
                                                value={patientForm.status}
                                                onChange={handleFormChange}
                                            >
                                                <MenuItem value='draft'>Draft</MenuItem>
                                                <MenuItem value='treatment'>Treatment</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="MRN"
                                            name='mrn'
                                            variant="outlined"
                                            value={patientForm.mrn || ''}
                                            onChange={handleFormChange}
                                            sx={{width: '100%'}}
                                        />
                                    </Box>
                                    <Box className='d-flex flex-column gap-3 flex-grow-1'>
                                        <TextField
                                            label="Date of Birth"
                                            name='dob'
                                            type='date'
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            value={patientForm.dob || ''}
                                            onChange={handleFormChange}
                                            sx={{width: '100%'}}
                                        />
                                        <TextField
                                            label="Location"
                                            name='location'
                                            variant="outlined"
                                            value={patientForm.location || ''}
                                            onChange={handleFormChange}
                                            sx={{width: '100%'}}
                                        />
                                        <Box className='d-flex flex-column'>
                                            <Typography className='p-0' variant="caption" component="span">Description</Typography>
                                            <TextareaAutosize
                                                name='description'
                                                minRows={7}
                                                value={patientForm.description || ''}
                                                onChange={handleFormChange}
                                                style={{width: '100%'}}
                                            />
                                        </Box>
                                        
                                    </Box>
                                </Box>
                                <Box className='mt-3 d-flex justify-content-center'>
                                    <Button
                                        className='px-4'
                                        variant="contained"
                                        onClick={handleSavePatient}
                                        disabled={isFormLoading || !patientForm.patient_id.length}
                                    >
                                        {isFormLoading ? <CircularProgress size={24.5} /> : 'Save'}
                                    </Button>
                                </Box>
                            </>
                    }
                </Box>
            </Modal>
        </Box>
    )
}

export default PatientsList